import { Inject, Injectable } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { AppStoreConfig, PositionDetails } from '@ddt-web/data-service';
import { AdditionalAnalysisParams, WebUserDataConstants } from '@ddt-web/domain';
import { AssetResolveResponse } from '@ddt-web/pas';
import { StoreService } from '@ddt-web/store-service';
import { WwePortfolioPosition } from '@ddt-web/wealth-web';
import { combineLatest, Observable, of, zip } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

import { PasPortfolioPositionsUtil } from '../../../utils/pas-portfolio-positions.util';
import { PasApiParamsService } from '../pas-api-params.service';

/**
 * Service that processes the PAS security-details API response for portfolio analysis workflows/ applications (ERA, PA, Nile).
 * This is the default behaviour used via PasDataLoaderService. 
 * 
 * This supports:
 * - mapping the valid securities back to the portfolios in the analysis
 * - error handling based on the value of throwSecurityDetailsPartialPortfoliosErrors
 */
@Injectable()
export class PasResponseProcessingService {

	constructor(
		protected apiParamService: PasApiParamsService, // can move the method to the normal API service
		protected storeService: StoreService,
		@Inject(APP_STORE_CONFIG_TOKEN) protected storeConfig: AppStoreConfig
	) { }

	public getPortfoliosToLoad(
		ids: string[],
		positionDataFromApiCall?: AssetResolveResponse, // TO BE UPDATED
		throwSecurityDetailsPartialPortfoliosErrors?: boolean,
		additionalAnalysisParams?: AdditionalAnalysisParams,
		positionDetailsType: string = PositionDetails.SECURITY_DETAILS_API_TYPE.SECURITY_DETAILS
	): Observable<WwePortfolioPosition[]> {
		const store = this.storeConfig[PositionDetails.SECURITY_DETAILS_STORE_MAPPING[positionDetailsType]] || this.storeConfig.positionDetails;

		// custom response processing for asset classes
		if (positionDetailsType === PositionDetails.SECURITY_DETAILS_API_TYPE.ASSET_CLASSES) {
			const securities = Array.from(positionDataFromApiCall.assets.values());
			const assetClassesToLoad = securities.map(res => {
				return {
					id: `${WebUserDataConstants.SUBTYPE.ASSET_CLASS}:${String(res.clientId)}`,
					children: [res]
				};
			});
			return of(assetClassesToLoad as any);
		}
		// custom response processing for cases when the requested securities are not part of the analysed portfolios (e.g. recommended iShares ideas)
		if (positionDetailsType === PositionDetails.SECURITY_DETAILS_API_TYPE.SECURITY_DETAILS_BLK_IDEAS) {
			// same as transient
			let portfoliosToLoad = PasPortfolioPositionsUtil.getPortfoliosToLoadAsTransientAnalysis(ids[0], positionDataFromApiCall, additionalAnalysisParams);

			// check missing securities 
			const missingSecurities = Array.from(positionDataFromApiCall?.assets?.keys() ?? []);

			if (missingSecurities.length && !portfoliosToLoad[0].children.length) { // all securities failed
				portfoliosToLoad = [];
			}

			return of(portfoliosToLoad);
		}
		return combineLatest([
			this.storeService.selectAll(store),
			this.storeService.selectAll(this.storeConfig.associatedPortfolios)
		]).pipe(
			first(),
			switchMap(([positionDetailsResult, associatedPortfoliosResult]) => {
				return zip(
					of(associatedPortfoliosResult),
					of(positionDetailsResult),
					of(positionDataFromApiCall),
					this.apiParamService.getAllActiveUniquePortfolioIds(ids, this.storeConfig),
					this.apiParamService.flattenPortfolios(ids, this.storeConfig, true)
				).pipe(first());
			}),
			map(([associatedPortfoliosResult, positionDetailsResult, positionDataFromApiCall, allPortfolioIds, flattenedPortfolios]) => {
				// Load/update position details in the store, for the given portfolios. Updates occur if there are any weight changes or security changes.
				const mappedPortfoliosWithMissingSecurities: Array<{ position: WwePortfolioPosition; missingSecurities: Array<string> }> =
					PasPortfolioPositionsUtil.mapPositionsToPortfolios(
						allPortfolioIds,
						positionDataFromApiCall,
						positionDetailsResult,
						associatedPortfoliosResult,
						throwSecurityDetailsPartialPortfoliosErrors,
						additionalAnalysisParams
					);

				// flattenedPortfolios
				const missingSecurities = [].concat(...mappedPortfoliosWithMissingSecurities.map(portfolio => portfolio.missingSecurities));

				let portfoliosToLoad = mappedPortfoliosWithMissingSecurities.map(portfolio => portfolio.position);
				if (
					!throwSecurityDetailsPartialPortfoliosErrors && additionalAnalysisParams?.identifierToBeUsedAsWweApiParamId
					&& positionDetailsType !== PositionDetails.SECURITY_DETAILS_API_TYPE.SECURITY_DETAILS_TAX
				) {
					const failedPortfolios = flattenedPortfolios
						.filter(portfolio =>
							portfolio['positions'].some(position => missingSecurities.includes(position[additionalAnalysisParams.identifierToBeUsedAsWweApiParamId]))
						)
						.map(portfolio => portfolio['id']);

					if (failedPortfolios.length) {
						portfoliosToLoad = portfoliosToLoad.filter(portfolio => !failedPortfolios.includes(portfolio.id));
					}
				}
				return portfoliosToLoad;
			})
		);
	}
}
